import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getWorkItems, setTokenProvider } from '../api/client';
import { useHrAccess } from '../pages/hr/HrAccessContext';
import { useVendorAccess } from '../pages/settings/vendorAccess';
import { useHqAccess } from '../pages/settings/hqAccess';
import { LocalCallerIndicator } from '../pages/hr/LocalCallerIndicator';
import { useAttentionSync, useMyAttention, useAdminAttention } from '../pages/hr/attentionStore';
import { badgeFor, correctionBadgeFor } from '../pages/hr/workspace/attentionModel';
import { AttentionBadge } from '../pages/hr/workspace/AttentionBadge';
import { FeedbackButton } from './feedback/FeedbackButton';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'unique-ai-sidebar-collapsed';

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  // BUILD-039: the navigation follows the person's CURRENT server-resolved Unique HQ permissions (role + individual permissions), not the
  // sign-in group in their token. Navigation is convenience only: every page and call is authorized again by the server.
  const hq = useHqAccess();
  const isAdmin = hq.can('hq.users.manage') || hq.canManageAccess;
  const showHqAdministration = isAdmin || ['hq.integrations.view', 'hq.automations.view', 'hq.supplier-pricing.admin', 'hq.documents.manage'].some((k) => hq.can(k));
  // BUILD-043: designated office staff hold a Vendors level (View / Maintain / Administer) without being in the admin group. The sidebar link follows what the server says they hold;
  // the page and every call are still authorized by the server on each request.
  const vendorAccess = useVendorAccess();
  const isOwner = hq.can('hq.attention.view');
  const showOwnerNotes = hq.can('hq.notes.use');
  const [workCount, setWorkCount] = useState(0);
  // Approved visual refresh (2026-09-27): expanded/collapsed sidebar state, remembered per
  // browser (matches the existing localStorage-preference pattern already used for density/sort
  // in MyWorkPage.tsx/AttentionQueuePage.tsx — no new persistence mechanism introduced).
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  useEffect(() => {
    setTokenProvider(getToken);
    loadWorkCount();
    // Refresh count every 60 seconds
    const interval = setInterval(loadWorkCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  async function loadWorkCount() {
    try {
      const data = await getWorkItems();
      const active = data.items.filter((i) => i.status !== 'completed').length;
      setWorkCount(active);
    } catch { /* silent */ }
  }

  // Primary work-area destinations (unchanged routes/permissions — only presentation moved from
  // a top nav bar into the grouped left sidebar). HR keeps its existing position, right after
  // My Work, exactly as design.md §7 specifies, and now appears unconditionally for every
  // authenticated HQ user ("basic HR administration" increment, 2026-10-06 reconciliation,
  // decision outcome A: "Show HR immediately in the authenticated HQ navigation... Menu
  // presence grants no protected access"). The prior version waited on GET /hr/me and hid the
  // entry entirely on denial/error, which delayed HR's appearance behind a network round trip
  // and could make it vanish on a transient failure. HrPage.tsx is now the ONLY place that
  // resolves Employee mapping/capabilities (with its own truthful loading/denied/retry/no-
  // surface states) — AppLayout no longer duplicates that call or that authorization decision.
  // Home and My Work render explicitly above the HR dropdown (see nav markup below); HR itself
  // is no longer a flat entry in this array — see HrNavGroup.
  const navItems = [
    // Attention Queue is a DEV preview — owner-gated for now (unchanged).
    ...(isOwner ? [{ to: '/attention', label: 'Attention', icon: BellIcon, badge: 0 }] : []),
    { to: '/business-health', label: 'Business Health', icon: HealthIcon, badge: 0 },
  ];
  const resourceItems = [
    { to: '/knowledge', label: 'Knowledge', icon: KnowledgeIcon, badge: 0 },
    { to: '/library', label: 'Library', icon: LibraryIcon, badge: 0 },
    ...(showOwnerNotes ? [{ to: '/owner/notes', label: 'Notes', icon: NotesIcon, badge: 0 }] : []),
  ];

  const userName = user?.name || user?.email || 'Account';
  const userEmail = user?.email || '';

  // Close the account menu on outside click / Escape, matching standard menu accessibility
  // (the prior top-bar had no popover menu at all, so there is no existing pattern to match).
  useEffect(() => {
    if (!accountMenuOpen) return;
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') setAccountMenuOpen(false); }
    function handleClick(e: MouseEvent) {
      if (!(e.target as HTMLElement).closest('[data-account-menu]')) setAccountMenuOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [accountMenuOpen]);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Full-width top header — spans above the sidebar too (approved visual refresh). Compact
          desktop height (~56px) per the accepted direction; the mockup's taller header was
          explicitly not a required dimension. */}
      <header className="bg-[#12223f] px-3 sm:px-5 h-14 flex items-center justify-between flex-shrink-0 border-b border-white/10">
        <NavLink to="/" className="flex items-center flex-shrink-0" aria-label="Unique HQ home">
          <img src="/assets/hq-wordmark.png" alt="Unique HQ" className="h-7 sm:h-8 w-auto" />
        </NavLink>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* 2026-10-13 UAT: the visual-only "Viewing as: Employee / HR Admin" simulation toggle
              was removed from the header — it was being confused with a real view switch. The
              single, truthful identity control in local walkthrough is the "Local backend caller"
              switcher below, which actually changes who the backend resolves and (now) refreshes
              the HR sidebar surfaces so the nav matches that identity. */}

          {/* October 8 21:19 addendum root-cause fix: a visible, local-only indicator/switcher
              for which employeeId the DISPOSABLE LOCAL WALKTHROUGH BACKEND currently resolves
              as the caller — distinct from, and previously invisible alongside, this app's own
              fixed "Local Reviewer" mock display name above. See LocalCallerIndicator.tsx's own
              header for the full root-cause explanation. Statically removed from any real
              build by the same VITE_LOCAL_MOCK_AUTH guard AuthContext.tsx already uses. */}
          <LocalCallerIndicator />

          {/* Feedback (Bug / Idea). HQ-local: reports go to HQ's own store and the HQ Owner's own inbox. Sits next to the bell. */}
          <FeedbackButton variant="header" />

          {/* Notifications — no notification popover exists in this app yet. Per the approved
              scope, the bell opens the existing Attention destination for accounts that have
              it (Owner-gated today); for everyone else there is genuinely no real destination
              to send them to, so the control is disabled rather than wired to an invented one. */}
          <button
            type="button"
            aria-label="Notifications"
            title={isOwner ? 'Notifications (opens Attention)' : 'No notifications destination available for this account'}
            disabled={!isOwner}
            onClick={() => { if (isOwner) navigate('/attention'); }}
            className={`p-2 rounded-md transition-colors ${
              isOwner ? 'text-gray-300 hover:bg-white/10 hover:text-white' : 'text-gray-600 cursor-not-allowed'
            }`}
          >
            <BellIcon className="w-5 h-5" />
          </button>

          <div className="relative" data-account-menu>
            <button
              type="button"
              onClick={() => setAccountMenuOpen((v) => !v)}
              aria-expanded={accountMenuOpen}
              aria-label={`${userName}, account menu`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-gray-200 hover:bg-white/10 transition-colors"
            >
              <span className="hidden sm:block text-right leading-tight">
                <span className="block text-sm font-medium text-white">{userName}</span>
                {userEmail && <span className="block text-[11px] text-gray-400">{userEmail}</span>}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            </button>
            {accountMenuOpen && (
              <div className="absolute right-0 top-11 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-20">
                <NavLink
                  to="/settings"
                  onClick={() => setAccountMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  My Profile
                </NavLink>
                <button
                  type="button"
                  onClick={() => { setAccountMenuOpen(false); logout(); navigate('/'); }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Brand Accent Ribbon */}
      <div className="h-1 flex-shrink-0 bg-gradient-to-r from-[#B8996B] via-[#4AABDE] to-[#B8996B]" />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Left sidebar — grouped work-area destinations. Expanded/collapsed state persists in
            localStorage; Collapse/Expand sits at the very bottom, below Settings. */}
        <aside
          className={`flex-shrink-0 bg-[#1a2b3d] text-gray-300 flex flex-col transition-[width] duration-150 ${collapsed ? 'w-16' : 'w-56'}`}
          aria-label="Primary navigation"
        >
          <nav className="flex-1 overflow-y-auto py-4 px-2" aria-label="Work areas">
            {!collapsed && <div className="px-3 pb-2 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">Workspace</div>}
            <SidebarLink to="/" label="Home" icon={HomeIcon} badge={0} collapsed={collapsed} />
            <SidebarLink to="/my-work" label="My Work" icon={WorkIcon} badge={workCount} collapsed={collapsed} />
            {/* UAT visual cleanup (2026-10-05): HR moved from a flat sidebar link into a grouped
                dropdown — the caller clicks "HR" to expand it, then picks My HR / My Team / HR
                Administration, matching the request to put HR's sub-views in the sidebar rather
                than a horizontal tab row on the HR page itself. Sub-items shown are driven by
                the SAME GET /hr/me resolution the HR page itself uses (HrAccessContext) — never
                an independent authorization decision; a sub-item appearing here is nav
                convenience only, exactly like every other link in this sidebar
                (requirements AZ-10). */}
            <HrNavGroup collapsed={collapsed} />
            {/* BUILD-043 (Owner, 2026-10-17 16:11): Vendors is a Workspace destination directly below HR, no longer a Settings tab.
                Shown only to the same people who could open it in Settings (owner / admin groups). That is NAV CONVENIENCE ONLY:
                the page and every vendor call are still authorized by the server (Company Admin Employee check), exactly as before. */}
            {(isAdmin || vendorAccess.me.level !== 'none') && <SidebarLink to="/vendors" label="Vendors" icon={VendorsIcon} badge={0} collapsed={collapsed} />}
            {navItems.map((item) => (
              <SidebarLink key={item.to} {...item} collapsed={collapsed} />
            ))}
            {!collapsed && <div className="px-3 pt-5 pb-2 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">Resources</div>}
            {resourceItems.map((item) => (
              <SidebarLink key={item.to} {...item} collapsed={collapsed} />
            ))}
          </nav>

          {/* HQ administration and Settings are distinct destinations, unchanged permissions,
              grouped at the bottom of the sidebar per the approved reference. */}
          <div className="px-2 pt-2 border-t border-white/10">
            {showHqAdministration && (
              <SidebarLink to="/admin" label="HQ administration" icon={ShieldIcon} badge={0} collapsed={collapsed} />
            )}
            <SidebarLink to="/settings" label="Settings" icon={SettingsIcon} badge={0} collapsed={collapsed} />
          </div>

          {/* Collapse/Expand — always last, below Settings. */}
          <div className="px-2 pb-3 pt-1">
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
            >
              <CollapseIcon className="w-4 h-4 flex-shrink-0" collapsed={collapsed} />
              {!collapsed && <span>Collapse navigation</span>}
            </button>
          </div>
        </aside>

        {/* Page Content */}
        <main className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

// UAT visual cleanup (2026-10-05): grouped HR dropdown, replacing the single flat "HR" sidebar
// link + the HR page's own horizontal tab row. Sub-items are exactly the caller's resolved real
// surfaces (My HR / My Team / HR Administration) — same list HrPage.tsx itself would have shown
// as tabs, just relocated and read from the shared HrAccessContext instead of a page-local
// fetch. The local synthetic preview intentionally has NO entry here — it is being retired from
// primary navigation per this cleanup; it remains reachable only via its own direct URL
// (/hr/preview) for as long as VITE_ENABLE_LOCAL_PREVIEW stays on in a given .env.
function HrNavGroup({ collapsed }: { collapsed: boolean }) {
  const { state } = useHrAccess();
  const location = useLocation();
  // BUILD-042 (Oct 17): server-backed "needs attention" counts. Employee (My HR) and HR (HR Administration) counts are separate and are
  // never added together. The group keeps them fresh (route change, focus, 60s, after any /hr write). Zero or unknown draws nothing;
  // a failed check draws "!" - never a 0. The server scopes every number to the caller's authority; this only decides what to ask for.
  // Oct 19 06:28: a profile-correction reviewer who is NOT an HR administrator also reads the admin slice (the server scopes it to
  // the requests they may see), so their Correction requests entry can carry its own badge. `mayReviewCorrections` is the server's EFFECTIVE flag.
  const isHrAdmin = state.kind === 'ready' && state.me.surfaces.hrAdmin;
  const isReviewerOnly = state.kind === 'ready' && !state.me.surfaces.hrAdmin && !!state.me.surfaces.mayReviewCorrections;
  useAttentionSync(state.kind === 'ready' ? { myHr: state.me.surfaces.myHr, hrAdmin: isHrAdmin || isReviewerOnly } : null);
  const myAttention = useMyAttention();
  const adminAttention = useAdminAttention();
  const myBadge = badgeFor(myAttention, 'My HR');
  // For a reviewer who is not an administrator only the correction-request count is theirs to see (never a document/task total).
  const adminBadge = isReviewerOnly ? correctionBadgeFor(adminAttention, 'Correction requests') : badgeFor(adminAttention, 'HR Administration');
  const anyAttention = myBadge.show || adminBadge.show;
  const isOnHr = location.pathname.startsWith('/hr') && !location.pathname.startsWith('/hr/preview');
  const [open, setOpen] = useState(isOnHr);

  // Keep the group expanded while the caller is actually on an HR screen, even if they
  // collapse/re-expand it manually elsewhere — avoids losing your place after a page refresh.
  useEffect(() => { if (isOnHr) setOpen(true); }, [isOnHr]);

  // Directory (2026-10-07, Owner-requested): "a company directory for everyone to see
  // employees." Unlike the other three sub-items, this is NOT gated on any surfaces.* flag —
  // it is meant to be visible to every authenticated HQ user, same open-visibility reasoning
  // the 2026-10-06 reconciliation already applied to showing the "HR" entry itself. The route
  // currently renders only an honest placeholder (DirectoryTab.tsx) — menu presence here grants
  // no protected access regardless, per this sidebar's existing convention (requirements AZ-10).
  const subItems =
    state.kind === 'ready'
      ? [
          state.me.surfaces.myHr ? { to: '/hr', label: 'My HR', end: true } : null,
          state.me.surfaces.myTeam ? { to: '/hr/team', label: 'My Team', end: false } : null,
          state.me.surfaces.hrAdmin ? { to: '/hr/admin', label: 'HR Administration', end: false } : null,
          // Reviewer entry (Oct 19 06:28): shown ONLY to an effective correction reviewer who is not an HR administrator. It reaches the
          // correction queue and its pending-work list and nothing else; menu presence grants no access (the server re-checks every request).
          !state.me.surfaces.hrAdmin && state.me.surfaces.mayReviewCorrections ? { to: '/hr/corrections', label: 'Correction requests', end: true } : null,
          !state.me.surfaces.hrAdmin && state.me.surfaces.mayReviewCorrections ? { to: '/hr/corrections/pending', label: 'Pending work', end: false } : null,
          // Benefit manager entry (Oct 23): shown ONLY to an effective benefit manager who is not an HR administrator (an administrator has Benefit plans inside
          // HR Administration). It reaches the benefit plan and eligibility screens and nothing else; menu presence grants no access (the server re-checks).
          !state.me.surfaces.hrAdmin && state.me.surfaces.mayManageBenefits ? { to: '/hr/benefits', label: 'Benefits', end: false } : null,
          { to: '/hr/directory', label: 'Directory', end: false },
        ].filter((i): i is { to: string; label: string; end: boolean } => !!i)
      : state.kind === 'denied' || state.kind === 'unavailable' || state.kind === 'error'
      // Directory stays reachable even if GET /hr/me itself failed/denied — it does not depend
      // on that resolution the way My HR/My Team/HR Administration intentionally do.
      ? [{ to: '/hr/directory', label: 'Directory', end: false }]
      : [];

  if (collapsed) {
    // Collapsed rail: a single icon linking straight to the caller's default HR surface,
    // matching how every other collapsed sidebar entry behaves (no flyout submenu exists in
    // this app yet — consistent with the rest of this sidebar's collapsed behavior).
    const defaultTo = subItems[0]?.to ?? '/hr';
    return (
      <div className="relative">
        <SidebarLink to={defaultTo} label="HR" icon={HrIcon} badge={0} collapsed={collapsed} />
        {anyAttention && (
          <span
            role="img"
            aria-label={`HR: ${[myBadge.show ? myBadge.label : '', adminBadge.show ? adminBadge.label : ''].filter(Boolean).join('; ')}`}
            title="Something in HR needs attention"
            data-testid="hr-rail-dot"
            className={`absolute top-1 right-2 w-2.5 h-2.5 rounded-full pointer-events-none ${myBadge.tone === 'error' || adminBadge.tone === 'error' ? 'bg-amber-500' : 'bg-red-500'}`}
          />
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors mb-0.5 ${
          isOnHr ? 'text-white bg-white/5' : 'text-gray-300 hover:bg-white/10 hover:text-white'
        }`}
      >
        <HrIcon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 text-left">HR</span>
        {/* Open: the numbered badges sit on the two sub-items below. Closed: a dot says "something inside needs attention" without
            adding the two separate counts together. */}
        {!open && anyAttention && (
          <span
            role="img"
            aria-label={`HR: ${[myBadge.show ? myBadge.label : '', adminBadge.show ? adminBadge.label : ''].filter(Boolean).join('; ')}`}
            data-testid="hr-group-dot"
            className={`w-2 h-2 rounded-full ${myBadge.tone === 'error' || adminBadge.tone === 'error' ? 'bg-amber-500' : 'bg-red-500'}`}
          />
        )}
        <ChevronDownIcon className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="ml-4 pl-3 border-l border-white/10 mb-1">
          {state.kind === 'loading' && <div className="px-3 py-1.5 text-xs text-gray-500">Loading…</div>}
          {(state.kind === 'denied' || state.kind === 'unavailable' || state.kind === 'error') && (
            <div className="px-3 py-1.5 text-xs text-gray-500">HR access unavailable</div>
          )}
          {state.kind === 'ready' && subItems.length === 0 && (
            <div className="px-3 py-1.5 text-xs text-gray-500">No HR access yet</div>
          )}
          {subItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `block px-3 py-1.5 rounded-md text-sm transition-colors mb-0.5 ${
                  isActive ? 'bg-[#4AABDE] text-white font-medium' : 'text-gray-400 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <span className="flex items-center justify-between gap-2">
                <span>{item.label}</span>
                {item.to === '/hr' && <AttentionBadge view={myBadge} testId="nav-badge-my-hr" />}
                {item.to === '/hr/admin' && <AttentionBadge view={adminBadge} testId="nav-badge-hr-admin" />}
                {item.to === '/hr/corrections' && <AttentionBadge view={adminBadge} testId="nav-badge-corrections" />}
              </span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarLink({
  to, label, icon: Icon, badge, collapsed,
}: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; badge: number; collapsed: boolean }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      title={collapsed ? label : undefined}
      aria-label={label}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors relative mb-0.5 ${
          collapsed ? 'justify-center' : ''
        } ${isActive ? 'bg-[#4AABDE] text-white' : 'text-gray-300 hover:bg-white/10 hover:text-white'}`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && <span>{label}</span>}
      {badge > 0 && (
        <span
          className={`absolute bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${
            collapsed ? '-top-0.5 -right-0.5' : 'top-1.5 right-2'
          }`}
        >
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </NavLink>
  );
}

// Simple inline SVG icons
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function WorkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
    </svg>
  );
}

function KnowledgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
  );
}

function HealthIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function HrIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  );
}

function VendorsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15l-.75 6a3 3 0 0 1-3 2.6 3 3 0 0 1-2.25-1 3 3 0 0 1-4.5 0 3 3 0 0 1-2.25 1 3 3 0 0 1-3-2.6L4.5 3Zm1.5 8.5V21m12-9.5V21M9.75 21v-5.25a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21" />
    </svg>
  );
}

function LibraryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function NotesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 4.556-4.03 8.25-9 8.25S3 16.556 3 12c0-.34.02-.675.058-1.006A11.954 11.954 0 0 0 12 2.25c3.72 0 7.196 1.6 9.542 4.244.297.335.55.706.75 1.104A8.03 8.03 0 0 1 21 12Z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function CollapseIcon({ className, collapsed }: { className?: string; collapsed: boolean }) {
  // panel-left-close / panel-left-open equivalent, flips direction based on state.
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <rect x="3" y="4" width="18" height="16" rx="2" strokeLinejoin="round" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 4v16" />
      <path strokeLinecap="round" strokeLinejoin="round" d={collapsed ? 'M14 9l2 3-2 3' : 'M16 9l-2 3 2 3'} />
    </svg>
  );
}
